import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Play, Pause, Calendar, DollarSign, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const CampaignManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  
  const [formData, setFormData] = useState({
    brand_id: '',
    campaign_name: '',
    description: '',
    start_date: '',
    end_date: '',
    budget: '',
    pricing_model: 'Fixed',
    cpm_rate: '',
    cpc_rate: '',
    monthly_rate: '',
    priority: 1
  });

  useEffect(() => {
    fetchBrands();
    fetchCampaigns();
  }, [filterStatus, filterBrand]);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/brands?approval_status=approved`);
      setBrands(response.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      let url = `${backendUrl}/api/admin/campaigns`;
      const params = [];
      if (filterStatus !== 'all') params.push(`status=${filterStatus}`);
      if (filterBrand !== 'all') params.push(`brand_id=${filterBrand}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast({
        title: 'Error',
        description: 'Failed to load campaigns',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare data based on pricing model
    const payload = {
      ...formData,
      budget: parseFloat(formData.budget),
      priority: parseInt(formData.priority)
    };
    
    // Only include relevant pricing fields
    if (formData.pricing_model === 'CPM') {
      payload.cpm_rate = parseFloat(formData.cpm_rate);
      payload.cpc_rate = null;
      payload.monthly_rate = null;
    } else if (formData.pricing_model === 'CPC') {
      payload.cpc_rate = parseFloat(formData.cpc_rate);
      payload.cpm_rate = null;
      payload.monthly_rate = null;
    } else {
      payload.monthly_rate = parseFloat(formData.monthly_rate);
      payload.cpm_rate = null;
      payload.cpc_rate = null;
    }
    
    try {
      if (selectedCampaign) {
        await axios.put(`${backendUrl}/api/admin/campaigns/${selectedCampaign.id}`, payload);
        toast({ title: 'Success', description: 'Campaign updated successfully' });
      } else {
        await axios.post(`${backendUrl}/api/admin/campaigns`, payload);
        toast({ title: 'Success', description: 'Campaign created successfully' });
      }
      
      setDialogOpen(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save campaign',
        variant: 'destructive'
      });
    }
  };

  const handleStatusToggle = async (campaign) => {
    try {
      const newStatus = campaign.status === 'active' ? 'paused' : 'active';
      await axios.put(`${backendUrl}/api/admin/campaigns/${campaign.id}`, { status: newStatus });
      toast({ 
        title: 'Success', 
        description: `Campaign ${newStatus === 'active' ? 'activated' : 'paused'}` 
      });
      fetchCampaigns();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update campaign status',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Are you sure you want to archive this campaign? All associated creatives will also be archived. They can be restored later.')) return;
    
    try {
      await axios.delete(`${backendUrl}/api/admin/campaigns/${campaignId}`);
      toast({ title: 'Success', description: 'Campaign archived successfully' });
      fetchCampaigns();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete campaign',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      brand_id: campaign.brand_id,
      campaign_name: campaign.campaign_name,
      description: campaign.description || '',
      start_date: campaign.start_date,
      end_date: campaign.end_date,
      budget: campaign.budget,
      pricing_model: campaign.pricing_model,
      cpm_rate: campaign.cpm_rate || '',
      cpc_rate: campaign.cpc_rate || '',
      monthly_rate: campaign.monthly_rate || '',
      priority: campaign.priority
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedCampaign(null);
    setFormData({
      brand_id: '',
      campaign_name: '',
      description: '',
      start_date: '',
      end_date: '',
      budget: '',
      pricing_model: 'Fixed',
      cpm_rate: '',
      cpc_rate: '',
      monthly_rate: '',
      priority: 1
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const calculateCTR = (clicks, impressions) => {
    if (impressions === 0) return '0.00';
    return ((clicks / impressions) * 100).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campaign Management</h1>
          <p className="text-gray-600 mt-1">Manage advertising campaigns</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus size={16} className="mr-2" />
              Add Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedCampaign ? 'Edit Campaign' : 'Create New Campaign'}</DialogTitle>
              <DialogDescription>
                {selectedCampaign ? 'Update campaign details' : 'Add a new advertising campaign'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="brand_id">Brand *</Label>
                <Select
                  value={formData.brand_id}
                  onValueChange={(value) => setFormData({ ...formData, brand_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>
                        {brand.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="campaign_name">Campaign Name *</Label>
                <Input
                  id="campaign_name"
                  name="campaign_name"
                  value={formData.campaign_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Summer 2024 Promotion"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Brief description of the campaign"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    name="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget">Budget (ZAR) *</Label>
                  <Input
                    id="budget"
                    name="budget"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.budget}
                    onChange={handleInputChange}
                    placeholder="10000.00"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="priority">Priority (1-10) *</Label>
                  <Input
                    id="priority"
                    name="priority"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.priority}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="pricing_model">Pricing Model *</Label>
                <Select
                  value={formData.pricing_model}
                  onValueChange={(value) => setFormData({ ...formData, pricing_model: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CPM">CPM (Cost Per Thousand Impressions)</SelectItem>
                    <SelectItem value="CPC">CPC (Cost Per Click)</SelectItem>
                    <SelectItem value="Fixed">Fixed Monthly Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.pricing_model === 'CPM' && (
                <div>
                  <Label htmlFor="cpm_rate">CPM Rate (ZAR) *</Label>
                  <Input
                    id="cpm_rate"
                    name="cpm_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cpm_rate}
                    onChange={handleInputChange}
                    placeholder="50.00"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Cost per 1,000 impressions</p>
                </div>
              )}
              
              {formData.pricing_model === 'CPC' && (
                <div>
                  <Label htmlFor="cpc_rate">CPC Rate (ZAR) *</Label>
                  <Input
                    id="cpc_rate"
                    name="cpc_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cpc_rate}
                    onChange={handleInputChange}
                    placeholder="15.00"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Cost per click</p>
                </div>
              )}
              
              {formData.pricing_model === 'Fixed' && (
                <div>
                  <Label htmlFor="monthly_rate">Monthly Rate (ZAR) *</Label>
                  <Input
                    id="monthly_rate"
                    name="monthly_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthly_rate}
                    onChange={handleInputChange}
                    placeholder="10000.00"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Fixed monthly cost</p>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {selectedCampaign ? 'Update' : 'Create'} Campaign
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="flex gap-2 items-center">
              <Label>Status:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 items-center">
              <Label>Brand:</Label>
              <Select value={filterBrand} onValueChange={setFilterBrand}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No campaigns found</p>
            <p className="text-gray-500 text-sm mt-2">Create your first campaign to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{campaign.campaign_name}</h3>
                      {getStatusBadge(campaign.status)}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Brand:</strong> {campaign.brand_name}
                    </p>
                    
                    {campaign.description && (
                      <p className="text-gray-600 mb-4">{campaign.description}</p>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-xs text-gray-600 mb-1">Duration</p>
                        <p className="text-sm font-semibold">
                          {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-xs text-gray-600 mb-1">Budget</p>
                        <p className="text-sm font-semibold">{formatCurrency(campaign.budget)}</p>
                      </div>
                      
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-xs text-gray-600 mb-1">Pricing Model</p>
                        <p className="text-sm font-semibold">
                          {campaign.pricing_model}
                          {campaign.pricing_model === 'CPM' && ` (${formatCurrency(campaign.cpm_rate)})`}
                          {campaign.pricing_model === 'CPC' && ` (${formatCurrency(campaign.cpc_rate)})`}
                          {campaign.pricing_model === 'Fixed' && ` (${formatCurrency(campaign.monthly_rate)}/mo)`}
                        </p>
                      </div>
                      
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-xs text-gray-600 mb-1">Priority</p>
                        <p className="text-sm font-semibold">{campaign.priority}/10</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 p-4 bg-blue-50 rounded">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Impressions</p>
                        <p className="text-lg font-bold text-[#2c3e7d]">{campaign.impressions.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Clicks</p>
                        <p className="text-lg font-bold text-[#2c3e7d]">{campaign.clicks.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">CTR</p>
                        <p className="text-lg font-bold text-[#2c3e7d]">{calculateCTR(campaign.clicks, campaign.impressions)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Revenue</p>
                        <p className="text-lg font-bold text-[#2c3e7d]">{formatCurrency(campaign.revenue)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    {campaign.status !== 'completed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusToggle(campaign)}
                        title={campaign.status === 'active' ? 'Pause Campaign' : 'Activate Campaign'}
                      >
                        {campaign.status === 'active' ? (
                          <Pause size={16} />
                        ) : (
                          <Play size={16} />
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(campaign)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(campaign.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default CampaignManagement;
